import React, { useEffect, useState } from 'react';
import { Plus, Trash2, ChevronDown, ChevronRight, Package, UtensilsCrossed } from 'lucide-react';
import { adminApi } from '../lib/api';

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 10)}`;

// Éditeur visuel GUIDÉ des formats / groupes / choix / rendements d'une option structurée.
// Présentation uniquement — la logique, les noms de champs et le modèle (formats, groups,
// choices, recipe_id, yield_per_batch) sont inchangés.
// props: formats (array), onChange(formats)
export const StructuredFormatsEditor = ({ formats = [], onChange }) => {
  const [recipes, setRecipes] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    adminApi.getRecipes?.().then((r) => setRecipes(Array.isArray(r.data) ? r.data : (r.data?.recipes || []))).catch(() => setRecipes([]));
  }, []);

  const setFormats = (f) => onChange(f);
  const toggle = (id) => setOpen((o) => ({ ...o, [id]: o[id] === false }));
  const isOpen = (id) => open[id] !== false; // ouvert par défaut pour plus de lisibilité

  const addFormat = () => setFormats([...(formats || []), {
    format_id: uid('fmt'), name: '', price: 0, extra_duration_minutes: 0,
    included_batches: null, order: (formats?.length || 0), is_active: true, groups: [],
  }]);
  const updFormat = (fid, patch) => setFormats(formats.map((f) => f.format_id === fid ? { ...f, ...patch } : f));
  const delFormat = (fid) => setFormats(formats.filter((f) => f.format_id !== fid));

  const addGroup = (fid) => updFormat(fid, {
    groups: [...(formats.find((f) => f.format_id === fid).groups || []), {
      group_id: uid('grp'), name: '', min_select: 1, max_select: 1,
      order: 0, is_active: true, choices: [],
    }],
  });
  const updGroup = (fid, gid, patch) => {
    const f = formats.find((x) => x.format_id === fid);
    updFormat(fid, { groups: f.groups.map((g) => g.group_id === gid ? { ...g, ...patch } : g) });
  };
  const delGroup = (fid, gid) => {
    const f = formats.find((x) => x.format_id === fid);
    updFormat(fid, { groups: f.groups.filter((g) => g.group_id !== gid) });
  };

  const addChoice = (fid, gid) => {
    const f = formats.find((x) => x.format_id === fid);
    const g = f.groups.find((x) => x.group_id === gid);
    updGroup(fid, gid, { choices: [...(g.choices || []), {
      choice_id: uid('ch'), name: '', is_active: true, order: 0,
      recipe_id: null, yield_per_batch: null, yield_unit: '',
    }] });
  };
  const updChoice = (fid, gid, cid, patch) => {
    const f = formats.find((x) => x.format_id === fid);
    const g = f.groups.find((x) => x.group_id === gid);
    updGroup(fid, gid, { choices: g.choices.map((c) => c.choice_id === cid ? { ...c, ...patch } : c) });
  };
  const delChoice = (fid, gid, cid) => {
    const f = formats.find((x) => x.format_id === fid);
    const g = f.groups.find((x) => x.group_id === gid);
    updGroup(fid, gid, { choices: g.choices.filter((c) => c.choice_id !== cid) });
  };

  const Field = ({ label, help, children }) => (
    <div>
      <label className="block text-sm font-medium text-[#2A312C] mb-1">{label}</label>
      {children}
      {help && <p className="text-xs text-[#5C6A61] mt-1 leading-snug">{help}</p>}
    </div>
  );

  return (
    <div className="space-y-4" data-testid="structured-formats-editor">
      {/* Étape 2 — Formats */}
      <div className="rounded-xl bg-[#A5BBA8]/10 border border-[#A5BBA8]/30 p-3">
        <p className="text-sm font-semibold text-[#2A312C]">Étape 2 — Formats</p>
        <p className="text-xs text-[#5C6A61] mt-0.5">Créez un ou plusieurs formats (ex : Petit, Grand). Le prix et le temps proviennent du format choisi par le client.</p>
      </div>

      {(formats || []).map((f, fi) => (
        <div key={f.format_id} className="border border-[#E3DED5] rounded-xl bg-white overflow-hidden">
          {/* En-tête format */}
          <div className="flex items-center gap-2 p-3 bg-[#FBFAF7] border-b border-[#E3DED5]">
            <button type="button" onClick={() => toggle(f.format_id)} className="text-[#5C6A61]" aria-label="Déplier">
              {isOpen(f.format_id) ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#A5BBA8]/30 text-xs font-semibold text-[#2A312C]">{fi + 1}</span>
            <input
              type="text" placeholder="Nom du format (ex : Petit, Grand)" value={f.name}
              onChange={(e) => updFormat(f.format_id, { name: e.target.value })}
              className="input-default flex-1 py-1.5 font-medium" data-testid={`fmt-name-${f.format_id}`}
            />
            <label className="flex items-center gap-1.5 text-xs text-[#5C6A61] whitespace-nowrap">
              <input type="checkbox" checked={f.is_active !== false} onChange={(e) => updFormat(f.format_id, { is_active: e.target.checked })} /> Actif
            </label>
            <button type="button" onClick={() => delFormat(f.format_id)} className="p-1.5 hover:bg-[#C86B51]/15 rounded" aria-label="Supprimer le format">
              <Trash2 className="w-4 h-4 text-[#C86B51]" />
            </button>
          </div>

          {isOpen(f.format_id) && (
            <div className="p-4 space-y-5">
              {/* Paramètres du format */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Prix (€)" help="Prix facturé pour ce format.">
                  <input type="number" min={0} step={1} value={f.price}
                    onChange={(e) => updFormat(f.format_id, { price: parseFloat(e.target.value) || 0 })}
                    className="input-default py-1.5" data-testid={`fmt-price-${f.format_id}`} />
                </Field>
                <Field label="Temps supplémentaire (min)" help="Durée ajoutée à la prestation (0 = aucun).">
                  <input type="number" min={0} step={5} value={f.extra_duration_minutes}
                    onChange={(e) => updFormat(f.format_id, { extra_duration_minutes: Math.max(0, parseInt(e.target.value) || 0) })}
                    className="input-default py-1.5" />
                </Field>
                <Field label="Nombre de lots inclus" help="Nombre de préparations réalisées pour ce format. Laissez vide si non applicable.">
                  <input type="number" min={1} step={1} value={f.included_batches ?? ''}
                    onChange={(e) => updFormat(f.format_id, { included_batches: e.target.value === '' ? null : Math.max(1, parseInt(e.target.value) || 1) })}
                    className="input-default py-1.5" placeholder="ex : 1, 2…" data-testid={`fmt-batches-${f.format_id}`} />
                </Field>
              </div>

              {/* Étape 3 & 4 — Choix de recettes + règle de choix */}
              <div className="rounded-lg bg-[#C86B51]/5 border border-[#C86B51]/20 p-3">
                <p className="text-sm font-semibold text-[#2A312C]">Étape 3 & 4 — Choix de recettes et règle de choix</p>
                <p className="text-xs text-[#5C6A61] mt-0.5">Regroupez les recettes proposées pour ce format (ex : « Gâteaux », « Biscuits », ou simplement « Recette »), puis définissez combien le client doit en choisir.</p>
              </div>

              <div className="space-y-3">
                {(f.groups || []).map((g) => (
                  <div key={g.group_id} className="border border-[#E3DED5] rounded-lg p-3 bg-[#FBFAF7]">
                    <div className="flex items-center gap-2 mb-3">
                      <UtensilsCrossed className="w-4 h-4 text-[#A5BBA8]" />
                      <input type="text" placeholder="Nom du groupe (ex : Gâteaux, Biscuits, Recette)" value={g.name}
                        onChange={(e) => updGroup(f.format_id, g.group_id, { name: e.target.value })}
                        className="input-default flex-1 py-1 font-medium" data-testid={`grp-name-${g.group_id}`} />
                      <label className="flex items-center gap-1 text-xs text-[#5C6A61] whitespace-nowrap">
                        <input type="checkbox" checked={g.is_active !== false} onChange={(e) => updGroup(f.format_id, g.group_id, { is_active: e.target.checked })} /> Actif
                      </label>
                      <button type="button" onClick={() => delGroup(f.format_id, g.group_id)} className="p-1 hover:bg-[#C86B51]/15 rounded" aria-label="Supprimer le groupe">
                        <Trash2 className="w-3.5 h-3.5 text-[#C86B51]" />
                      </button>
                    </div>

                    {/* Choix de recettes */}
                    <p className="text-xs font-medium text-[#2A312C] mb-1">Recettes proposées</p>
                    <p className="text-xs text-[#5C6A61] mb-2">Choix de recettes : sélectionne les recettes déjà créées dans le catalogue.</p>
                    <div className="space-y-2 mb-3">
                      {(g.choices || []).map((c) => (
                        <div key={c.choice_id} className="bg-white border border-[#E3DED5] rounded-lg p-2" data-testid={`choice-row-${c.choice_id}`}>
                          <div className="flex flex-wrap items-center gap-2">
                            <select value={c.recipe_id || ''}
                              onChange={(e) => {
                                const rid = e.target.value || null;
                                const rec = recipes.find((r) => r.recipe_id === rid);
                                updChoice(f.format_id, g.group_id, c.choice_id, { recipe_id: rid, name: rec ? rec.name : '' });
                              }}
                              className="input-default py-1 flex-1 min-w-[200px]" data-testid={`ch-recipe-${c.choice_id}`}>
                              <option value="">— choisir une recette du catalogue —</option>
                              {recipes.map((r) => <option key={r.recipe_id} value={r.recipe_id}>{r.name}</option>)}
                            </select>
                            <div className="flex items-center gap-1">
                              <input type="number" min={0} step={1} placeholder="rdt" value={c.yield_per_batch ?? ''}
                                onChange={(e) => updChoice(f.format_id, g.group_id, c.choice_id, { yield_per_batch: e.target.value === '' ? null : parseFloat(e.target.value) })}
                                className="input-default py-1 w-16" data-testid={`ch-yield-${c.choice_id}`} title="Rendement par lot (affichage uniquement)" />
                              <input type="text" placeholder="unité" value={c.yield_unit || ''}
                                onChange={(e) => updChoice(f.format_id, g.group_id, c.choice_id, { yield_unit: e.target.value })}
                                className="input-default py-1 w-20" title="Unité (pots, parts, pièces…)" />
                            </div>
                            <label className="flex items-center gap-1 text-xs text-[#5C6A61]">
                              <input type="checkbox" checked={c.is_active !== false} onChange={(e) => updChoice(f.format_id, g.group_id, c.choice_id, { is_active: e.target.checked })} /> Actif
                            </label>
                            <button type="button" onClick={() => delChoice(f.format_id, g.group_id, c.choice_id)} className="p-1 hover:bg-[#C86B51]/15 rounded" aria-label="Supprimer la recette">
                              <Trash2 className="w-3.5 h-3.5 text-[#C86B51]" />
                            </button>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={() => addChoice(f.format_id, g.group_id)} className="text-xs text-[#A5BBA8] hover:underline flex items-center gap-1">
                        <Plus className="w-3 h-3" /> Ajouter une recette
                      </button>
                    </div>
                    <p className="text-xs text-[#5C6A61] -mt-1 mb-3 flex items-center gap-1">
                      <Package className="w-3 h-3" /> Rendement par lot : information affichée uniquement, ne modifie pas les quantités d&apos;ingrédients.
                    </p>

                    {/* Règle de choix */}
                    <div className="flex flex-wrap items-end gap-4 border-t border-[#E3DED5] pt-3">
                      <Field label="Choix minimum" help="Nombre minimum de recettes à choisir.">
                        <input type="number" min={0} value={g.min_select}
                          onChange={(e) => updGroup(f.format_id, g.group_id, { min_select: Math.max(0, parseInt(e.target.value) || 0) })}
                          className="input-default w-24 py-1" data-testid={`grp-min-${g.group_id}`} />
                      </Field>
                      <Field label="Choix maximum" help="Nombre maximum de recettes à choisir.">
                        <input type="number" min={1} value={g.max_select}
                          onChange={(e) => updGroup(f.format_id, g.group_id, { max_select: Math.max(1, parseInt(e.target.value) || 1) })}
                          className="input-default w-24 py-1" data-testid={`grp-max-${g.group_id}`} />
                      </Field>
                    </div>
                  </div>
                ))}
                <button type="button" onClick={() => addGroup(f.format_id)} className="btn-outline w-full text-sm py-2 flex items-center justify-center gap-1">
                  <Plus className="w-4 h-4" /> Ajouter un groupe de choix
                </button>
              </div>
            </div>
          )}
        </div>
      ))}

      <button type="button" onClick={addFormat} className="btn-primary w-full" data-testid="add-format-btn">
        <Plus className="w-4 h-4 mr-2" /> Ajouter un format
      </button>
    </div>
  );
};
